import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { normalizeAffiliateCode } from '@/lib/affiliateCode';

const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const REF_KEY = 'hpe_referral_code';
const REF_EXP_KEY = 'hpe_referral_code_exp';
const AFF_KEY = 'hpe_affiliate_code';
const AFF_EXP_KEY = 'hpe_affiliate_code_exp';

/** Read a stored code, clearing it if expired. */
function readWithExpiry(key: string, expKey: string): string | null {
  try {
    const value = localStorage.getItem(key);
    if (!value) return null;
    const expRaw = localStorage.getItem(expKey);
    if (expRaw) {
      const exp = parseInt(expRaw, 10);
      if (Number.isFinite(exp) && Date.now() > exp) {
        localStorage.removeItem(key);
        localStorage.removeItem(expKey);
        return null;
      }
    }
    return value;
  } catch {
    return null;
  }
}

function writeWithExpiry(key: string, expKey: string, value: string) {
  try {
    localStorage.setItem(key, value);
    localStorage.setItem(expKey, String(Date.now() + TTL_MS));
  } catch {
    /* storage may be unavailable (private mode) — ignore */
  }
}

function clearKey(key: string, expKey: string) {
  try {
    localStorage.removeItem(key);
    localStorage.removeItem(expKey);
  } catch {
    /* ignore */
  }
}

/**
 * Capture ?ref= / ?aff= attribution from the URL on first visit.
 * Persists with a 30-day expiry so the code survives until signup + checkout.
 * Does NOT strip the param from the URL (referral landing pages may read it directly).
 */
function captureReferralFromUrl() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);

    // Affiliate codes — Equalizer program / paid affiliate links.
    const affRaw =
      params.get('aff') ||
      params.get('affiliate') ||
      params.get('affiliate_code');
    const aff = normalizeAffiliateCode(affRaw);
    if (aff) {
      // First-touch attribution: only set if not already present (and not expired).
      const existing = readWithExpiry(AFF_KEY, AFF_EXP_KEY);
      if (!existing) writeWithExpiry(AFF_KEY, AFF_EXP_KEY, aff);
    }

    // Stylist invite / referral codes.
    const refRaw =
      params.get('ref') ||
      params.get('invite') ||
      params.get('referral') ||
      params.get('referral_code');
    if (refRaw) {
      const code = refRaw.trim().toUpperCase();
      if (code) {
        const existing = readWithExpiry(REF_KEY, REF_EXP_KEY);
        if (!existing) writeWithExpiry(REF_KEY, REF_EXP_KEY, code);
      }
    }
  } catch {
    /* malformed URL — ignore */
  }
}

/**
 * On sign-up, if a referral code was stored in localStorage,
 * look up the referring stylist and save the connection.
 */
export function useReferralOnSignup() {
  const processed = useRef(false);

  // 1) Capture ?ref / ?aff from the URL on first mount.
  useEffect(() => {
    captureReferralFromUrl();
  }, []);

  // 2) On sign-in, attribute the new user to the stored referrer.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event !== 'SIGNED_IN' || !session?.user || processed.current) return;

        const code = readWithExpiry(REF_KEY, REF_EXP_KEY);
        const affiliateCode = normalizeAffiliateCode(
          readWithExpiry(AFF_KEY, AFF_EXP_KEY)
        );

        processed.current = true;

        // ── Affiliate referral tracking ──
        if (affiliateCode) {
          try {
            const { error } = await supabase.functions.invoke('affiliate-referral', {
              body: {
                action: 'capture',
                code: affiliateCode,
              },
            });

            if (error) throw error;
            clearKey(AFF_KEY, AFF_EXP_KEY);
          } catch (err) {
            console.error('Affiliate referral error:', err);
          }
        }

        // ── Legacy stylist referral tracking ──
        if (!code) return;

        try {
          // Look up the referrer
          const { data: referrerRows } = await supabase
            .rpc('resolve_invite_code', { _code: code });
          const referrer = Array.isArray(referrerRows) ? referrerRows[0] : null;

          if (!referrer || referrer.user_id === session.user.id) {
            clearKey(REF_KEY, REF_EXP_KEY);
            return;
          }

          const { data: existingProfile } = await supabase
            .from('stylist_profiles')
            .select('id, referred_by')
            .eq('user_id', session.user.id)
            .maybeSingle();

          if (existingProfile) {
            if (!existingProfile.referred_by) {
              await supabase
                .from('stylist_profiles')
                .update({ referred_by: referrer.user_id })
                .eq('user_id', session.user.id);
            }
          }

          clearKey(REF_KEY, REF_EXP_KEY);
        } catch (err) {
          console.error('Referral processing error:', err);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);
}
